import { useSelector } from '@xstate/react';
import cn from 'classnames';
import { Fragment, useCallback, useEffect, useState } from 'react';
import { ProgressBar } from 'react-step-progress-bar';

import { Arrow } from '@webapp/common/assets/icons';
import { Checkbox } from '@webapp/common/components/checkbox';
import { CssTransitionItems } from '@webapp/common/components/css-transition-items/css-transition-items';
import { Loader } from '@webapp/common/components/loader';
import { calcPercent, setHtml } from '@webapp/common/lib/ui';

import { selectInfo } from '@webapp/survey/src/machine/selectors';
import { usePipingAnswersReplacer } from '@webapp/survey/src/machine/use-piping-answers-replacer';
import { useStateSelector } from '@webapp/survey/src/machine/use-state-selector';

import type * as lib from '../../lib';

import css from './max-diff.css';

export const MaxDiff: FC<lib.SurveyQuestion> = ({ question, actor }) => {
    const {
        design: {
            fonts: { elementColor },
        },
        params: {
            alert: { maxDiffBlock },
        },
    } = useStateSelector(selectInfo);
    const [worst, setWorst] = useState<null | number>(null);
    const [best, setBest] = useState<null | number>(null);
    const maxDiffActor = useSelector(actor, ({ context }) => context.maxDiffBlocksActor);
    const pipingReplacer = usePipingAnswersReplacer();
    const { blocks, answers, currentBlockIndex } = useSelector(maxDiffActor, ({ context }) => context);
    const [maxWidth, setMaxWidth] = useState<number | null>(null);
    const [maxWidthCalcRef, setMaxWidthCalcRef] = useState<HTMLDivElement | null>(null);
    const [leftLabelRef, setLeftLabelRef] = useState<HTMLDivElement | null>(null);
    const [rightLabelRef, setRightLabelRef] = useState<HTMLDivElement | null>(null);
    const [maxWidthByBundle, setMaxWidthByBundle] = useState<number | null>(null);

    useEffect(() => {
        const answer = answers[currentBlockIndex];
        setWorst(answer?.worst ?? null);
        setBest(answer?.best ?? null);
    }, [currentBlockIndex, answers]);

    useEffect(() => {
        const compute = () => {
            if (maxWidthCalcRef) {
                setMaxWidth(maxWidthCalcRef.clientWidth);
                const closest = maxWidthCalcRef.closest('.currentBundleNew') ?? maxWidthCalcRef.closest('.content');
                if (closest) {
                    const paddingLeft = parseFloat(getComputedStyle(closest).getPropertyValue('padding-left'));
                    const paddingRight = parseFloat(getComputedStyle(closest).getPropertyValue('padding-right'));
                    const rightLabelWidth = rightLabelRef?.clientWidth ?? 0;
                    const leftLabelWidth = leftLabelRef?.clientWidth ?? 0;

                    setMaxWidthByBundle(
                        closest.clientWidth - paddingLeft - paddingRight - rightLabelWidth - leftLabelWidth - 120,
                    );
                }
            }
        };
        compute();
        window.addEventListener('resize', compute);
        return () => {
            window.removeEventListener('resize', compute);
        };
    }, [leftLabelRef, maxWidthCalcRef, rightLabelRef]);

    const onAnswer = (type: 'best' | 'worst', answerId: number) => {
        if (type === 'best') {
            setBest(answerId);
        } else {
            setWorst(answerId);
        }
        const answer = answers[currentBlockIndex];
        if (answer) {
            return;
        }
        if (type === 'best') {
            if (worst) {
                setTimeout(() => {
                    maxDiffActor.send({
                        type: 'ADD_ANSWER',
                        best: answerId,
                        worst,
                    });
                }, 500);
            } else {
                setBest(answerId);
            }
        }
        if (type === 'worst') {
            if (best) {
                setTimeout(() => {
                    maxDiffActor.send({
                        type: 'ADD_ANSWER',
                        best,
                        worst: answerId,
                    });
                }, 500);
            } else {
                setWorst(answerId);
            }
        }
    };
    // Индикатор показывает количество отвеченных блоков, а не индекс текущего
    const answeredBlocksCount = Object.keys(answers).length;
    const getPercent = useCallback(
        () => calcPercent(answeredBlocksCount, blocks.length),
        [answeredBlocksCount, blocks.length],
    );

    if (blocks.length === 0)
        return (
            <div className={css.loader}>
                <Loader inline visible small />
            </div>
        );
    const currentBlock = blocks[currentBlockIndex];

    return (
        <div className={css.root}>
            <div className={css.progressBarWrap}>
                <span>{maxDiffBlock}</span>
                <div className={css.progressBarContainer}>
                    <span>
                        {answeredBlocksCount} / {blocks.length}
                    </span>
                    <div className={css.progressBar}>
                        <ProgressBar percent={getPercent()} unfilledBackground='#ccc' filledBackground={elementColor} />
                    </div>
                </div>
            </div>
            <div className={css.wrap}>
                <CssTransitionItems type='fade' items={blocks} activeItem={currentBlockIndex}>
                    <div className={css.maxDiff}>
                        <div className={css.label} ref={setLeftLabelRef}>
                            {question.params.worstLabel}
                        </div>
                        <div />
                        <div className={css.label} ref={setRightLabelRef}>
                            {question.params.bestLabel}
                        </div>
                        <div className={css.maxWidthCalc} ref={setMaxWidthCalcRef}>
                            {question.answersRaw.map((answer) => (
                                <div key={answer.id} {...setHtml(pipingReplacer(answer.value?.[0] || ''))} />
                            ))}
                        </div>
                        {currentBlock.map((block) => (
                            <Fragment key={`${block.answerId}-${currentBlockIndex}`}>
                                <Checkbox
                                    className={cn(
                                        css.leftCheckbox,
                                        worst && worst !== block.answerId && css.checkboxVisuallyDisabled,
                                    )}
                                    round
                                    checked={worst === block.answerId}
                                    disabled={best === block.answerId}
                                    onChange={() => onAnswer('worst', block.answerId)}
                                />
                                <div className={css.value1} {...setHtml(pipingReplacer(block.value1))} />
                                <div className={css.rightCheckbox}>
                                    <Checkbox
                                        className={cn(best && best !== block.answerId && css.checkboxVisuallyDisabled)}
                                        round
                                        checked={best === block.answerId}
                                        disabled={worst === block.answerId}
                                        onChange={() => onAnswer('best', block.answerId)}
                                    />
                                </div>
                            </Fragment>
                        ))}
                    </div>
                </CssTransitionItems>
                <CssTransitionItems type='fade' items={blocks} activeItem={currentBlockIndex}>
                    <div className={css.maxDiffMobile}>
                        <div className={css.label}>{question.params.worstLabel}</div>

                        {currentBlock.map((block, index) => (
                            <Checkbox
                                key={`${block.answerId}-${index}`}
                                className={cn(worst && worst !== block.answerId && css.checkboxVisuallyDisabled)}
                                round
                                checked={worst === block.answerId}
                                disabled={best === block.answerId}
                                onChange={() => onAnswer('worst', block.answerId)}
                                label={<span className={css.mobileAttr} {...setHtml(pipingReplacer(block.value1))} />}
                            />
                        ))}
                        <div className={css.label}>{question.params.bestLabel}</div>
                        {currentBlock.map((block, index) => (
                            <Checkbox
                                key={`${block.answerId}-${index}`}
                                round
                                className={cn(best && best !== block.answerId && css.checkboxVisuallyDisabled)}
                                checked={best === block.answerId}
                                disabled={worst === block.answerId}
                                onChange={() => onAnswer('best', block.answerId)}
                                label={<span className={css.mobileAttr} {...setHtml(pipingReplacer(block.value1))} />}
                            />
                        ))}
                    </div>
                </CssTransitionItems>
                <div className={css.arrows}>
                    <Arrow
                        className={cn(css.leftArrow, currentBlockIndex === 0 && css.leftArrowDisabled)}
                        onClick={() =>
                            maxDiffActor.send({
                                type: 'PREV_BLOCK',
                            })
                        }
                        color={elementColor}
                    />
                    <Arrow
                        className={cn(
                            css.rightArrow,
                            (currentBlockIndex === blocks.length - 1 || !best || !worst) && css.rightArrowDisabled,
                        )}
                        onClick={() =>
                            maxDiffActor.send({
                                type: 'NEXT_BLOCK',
                            })
                        }
                        color={elementColor}
                    />
                </div>
            </div>
        </div>
    );
};
